import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import {
    Container,
    Section,
    PageHero,
    AmberButton,
    CardImage,
} from "@/components/Primitives";

const STEPS = [
    {
        n: "1",
        title: "Screen the market.",
        body: "Before the open, The Aegis Line scans the market for names that earned their place today. Stocks gapping on real relative volume \u2014 measured against each name's own 20-day baseline, not an arbitrary number. A confirmed catalyst where one exists. Price in a tradeable range. Index futures setting up against their overnight range. Thousands of names filtered to a short, ranked candidate list.",
        tag: "code",
        img: "/images/cards/screen_market.png",
    },
    {
        n: "2",
        title: "Compute the structure.",
        body: "Each candidate runs through a sequence of deterministic analysis modules \u2014 VWAP position, EMA trend, momentum and volume balance, RSI, Bollinger structure, MACD, relative strength against the index, and the opening-range break. This is code. There is no AI here, and that's deliberate: the facts about the chart should be computed the same way every time, regardless of what the market is doing or how the session feels.",
        tag: "code",
        img: "/images/cards/compute_structure.png",
    },
    {
        n: "3",
        title: "Build a clean brief.",
        body: "The signals are merged, de-duplicated, and translated into plain structural language \u2014 one fact per idea, no redundant noise. Then the brief is cleaned again: anything that would pre-answer the trade is stripped out before the model sees it. No \u201cstrong setup\u201d labels. No recommended direction. The desk prepares the brief. It does not make the call.",
        tag: "code",
        img: "/images/cards/clean_brief.png",
    },
    {
        n: "4",
        title: "Make the call.",
        body: "A reasoning model reads the brief and decides: enter, wait, or pass \u2014 with a direction, a conviction level, an activation price copied exactly from the computed structure, and a written reason. The model cannot invent levels. It cannot wave away counter-risks as disclaimers. It is held to the same standard as the rest of the system.",
        tag: "model",
        img: "/images/cards/make_call.png",
    },
    {
        n: "5",
        title: "Plan the trade \u2014 deterministically.",
        body: "The decision goes back to code. Entry zone, stop, and tiered targets are computed from the key levels. The stop is never an arbitrary dollar amount \u2014 it scales with the instrument's own range and respects a volatility-based minimum. Position size scales with the model's conviction. The geometry is built by rules, not adjusted by feel.",
        tag: "code",
        img: "/images/cards/plan_trade.png",
    },
    {
        n: "6",
        title: "Manage to plan.",
        body: "The position is managed candle by candle. At each target the model re-decides \u2014 hold, take profit, or scale \u2014 rather than auto-exiting. Stops move to break-even after the first target and trail after the second. Every reassessment is logged with its reasoning.",
        tag: "model",
        img: "/images/cards/manage_plan.png",
    },
];

export default function HowItWorks() {
    return (
        <>
            <Seo
                title="How it works"
                description="The Aegis Line runs the same path a disciplined trading desk would — from premarket screening to a managed position, with code as the analyst and the model as the trader."
            />
            <PageHero
                eyebrow="The pipeline, explained for a human"
                title="From premarket to managed position."
                sub="The Aegis Line runs the same path a disciplined trading desk would — just faster, and without skipping steps. Here's the whole route."
            />

            <Section>
                <Container>
                    <ol className="relative space-y-5 border-l border-haze pl-6 sm:pl-10">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.05}>
                                <li className="group relative overflow-hidden rounded-lg border border-haze bg-ink-soft p-6 transition-colors hover:border-rule/40 sm:p-8">
                                    <span className="absolute -left-[31px] top-8 flex h-6 w-6 items-center justify-center rounded-full border border-rule bg-ink font-mono text-xs text-rule sm:-left-[51px]">
                                        {s.n}
                                    </span>
                                    <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
                                        <div className="flex-1">
                                            <div className="flex flex-wrap items-center gap-3">
                                                <h2 className="text-xl font-semibold text-text-hi">
                                                    {s.title}
                                                </h2>
                                                <span
                                                    className={`rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                                                        s.tag === "model"
                                                            ? "border-signal/40 text-signal"
                                                            : "border-rule/40 text-rule"
                                                    }`}
                                                >
                                                    {s.tag === "model"
                                                        ? "the trader"
                                                        : "the desk · code"}
                                                </span>
                                            </div>
                                            <p className="mt-3 text-sm leading-relaxed text-text-lo sm:text-base">
                                                {s.body}
                                            </p>
                                        </div>
                                        <CardImage
                                            src={s.img}
                                            alt={s.title}
                                            className="h-36 w-full shrink-0 rounded-md border border-haze sm:h-32 sm:w-56"
                                        />
                                    </div>
                                </li>
                            </Reveal>
                        ))}
                    </ol>

                    <Reveal>
                        <div className="mt-12 rounded-lg border border-rule/30 bg-ink-soft p-7">
                            <p className="text-base leading-relaxed text-text-hi sm:text-lg">
                                At every step, the standard holds.{" "}
                                <span className="font-mono text-rule">
                                    Code is the analyst, the model is the trader.
                                </span>{" "}
                                The analysis is reproducible. The judgment is
                                transparent. The decisions are written down. The
                                line holds because something is protecting it.
                            </p>
                            <div className="mt-6">
                                <AmberButton
                                    to="/methodology"
                                    testId="hiw-methodology-cta"
                                >
                                    Read the methodology{" "}
                                    <ArrowRight size={16} />
                                </AmberButton>
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </Section>
        </>
    );
}
